import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const songs = [
  { title: "At Last", file: "/music/At_Last.mp3", artist: "Etta James" },
  { title: "Beds Are Burning", file: "/music/Beds_are_Burning.mp3", artist: "Midnight Oil" },
  { title: "Epic", file: "/music/Epic.mp3", artist: "Faith No More" },
  { title: "Home Sweet Home", file: "/music/Home_Sweet_Home.mp3", artist: "Mötley Crüe" },
  { title: "Let's Stay Together", file: "/music/Lets_Stay_Together.mp3", artist: "Al Green" },
  { title: "Moondance", file: "/music/Moondance.mp3", artist: "Van Morrison" },
  { title: "Mr Blue Sky", file: "/music/Mr_Blue_Sky.mp3", artist: "ELO" },
  { title: "Power of Love", file: "/music/Power_of_Love.mp3", artist: "Huey Lewis" },
  { title: "Regulate", file: "/music/Regulate.mp3", artist: "Warren G ft. Nate Dogg" },
  { title: "Waiting Room", file: "/music/Waiting_Room.mp3", artist: "Fugazi" },
  { title: "Walking on Sunshine", file: "/music/Walking_on_Sunshine.mp3", artist: "Katrina & The Waves" },
  { title: "Wannabe", file: "/music/Wannabe.mp3", artist: "Spice Girls" }
];

export default function MusicPlayer() {
  const [isOpen, setIsOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrack, setCurrentTrack] = useState(songs[0]);
  const [volume, setVolume] = useState(0.7);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleLoadStart = () => setIsLoading(true);
    const handleCanPlay = () => setIsLoading(false);
    const handleEnded = () => {
      setIsPlaying(false);
      nextTrack();
    };

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [currentTrack]);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    
    try {
      if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
      } else {
        await audio.play();
        setIsPlaying(true);
      }
    } catch (error) {
      console.error('Audio playback failed:', error);
      setIsPlaying(false);
    }
  };

  const nextTrack = () => {
    const currentIndex = songs.findIndex(song => song.file === currentTrack.file);
    const nextIndex = (currentIndex + 1) % songs.length;
    setCurrentTrack(songs[nextIndex]);
    setIsPlaying(false);
  };

  const prevTrack = () => {
    const currentIndex = songs.findIndex(song => song.file === currentTrack.file);
    const prevIndex = currentIndex === 0 ? songs.length - 1 : currentIndex - 1;
    setCurrentTrack(songs[prevIndex]);
    setIsPlaying(false);
  };

  const changeTrack = (song) => {
    setCurrentTrack(song);
    setIsPlaying(false);
  };

  const changeVolume = (e) => {
    const vol = parseFloat(e.target.value);
    setVolume(vol);
    if (audioRef.current) {
      audioRef.current.volume = vol;
    }
  };

  const seek = (e) => {
    const audio = audioRef.current;
    if (audio && duration) {
      const clickX = e.nativeEvent.offsetX;
      const width = e.target.offsetWidth;
      const newTime = (clickX / width) * duration;
      audio.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const formatTime = (time) => {
    if (isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const toggleOpen = () => setIsOpen(!isOpen);

  return (
    <>
      {/* Toggle Button */}
      <motion.div
        className="fixed bottom-8 left-8 z-[9999]"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 500, damping: 30 }}
      >
        <motion.button
          onClick={toggleOpen}
          className="group relative w-16 h-16 rounded-full bg-gradient-to-br from-pink-500/90 to-purple-600/90 backdrop-blur-md border border-white/20 shadow-2xl overflow-hidden"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          {/* Animated background */}
          <div className="absolute inset-0 bg-gradient-to-br from-pink-400/20 to-purple-500/20 animate-pulse" />
          
          {/* Music note icon */}
          <div className="absolute inset-0 flex items-center justify-center">
            <motion.div
              className="text-white text-2xl"
              animate={{ rotate: isPlaying ? 360 : 0 }}
              transition={{ duration: 2, repeat: isPlaying ? Infinity : 0, ease: "linear" }}
            >
              🎵
            </motion.div>
          </div>

          {/* Pulsing ring when playing */}
          {isPlaying && (
            <motion.div
              className="absolute inset-0 rounded-full border-2 border-pink-300"
              animate={{ scale: [1, 1.3, 1], opacity: [1, 0, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          )}

          {/* Tooltip */}
          <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <div className="bg-black/80 text-white px-3 py-1 rounded-lg text-sm whitespace-nowrap backdrop-blur-sm">
              Pink Pixel Music 🎶
            </div>
          </div>
        </motion.button>
      </motion.div>

      {/* Music Player Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed bottom-16 left-28 z-[9998] w-80"
          >
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl overflow-hidden">
              {/* Header */}
              <div className="p-4 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <h3 className="text-white font-semibold text-lg flex items-center gap-2">
                    🎵 Pink Pixel Music
                  </h3>
                  <motion.button
                    onClick={toggleOpen}
                    className="text-white/70 hover:text-white text-xl"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    ✕
                  </motion.button>
                </div>
              </div>

              {/* Current Track Info */}
              <div className="p-4 text-center">
                <div className="text-white font-medium text-lg mb-1">{currentTrack.title}</div>
                <div className="text-white/70 text-sm mb-3">{currentTrack.artist}</div>
                
                {/* Progress Bar */}
                <div className="mb-2">
                  <div 
                    className="w-full h-2 bg-white/20 rounded-full cursor-pointer overflow-hidden"
                    onClick={seek}
                  >
                    <motion.div 
                      className="h-full bg-gradient-to-r from-pink-400 to-purple-500 rounded-full"
                      style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
                      transition={{ duration: 0.1 }}
                    />
                  </div>
                  <div className="flex justify-between text-white/70 text-xs mt-1">
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(duration)}</span>
                  </div>
                </div>
              </div>

              {/* Controls */}
              <div className="px-4 pb-4">
                <div className="flex items-center justify-center gap-4 mb-4">
                  <motion.button
                    onClick={prevTrack}
                    className="text-white/80 hover:text-white text-2xl"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    ⏮️
                  </motion.button>
                  
                  <motion.button
                    onClick={togglePlay}
                    className="w-12 h-12 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 flex items-center justify-center text-white text-xl shadow-lg"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    disabled={isLoading}
                  >
                    {isLoading ? '⏳' : (isPlaying ? '⏸️' : '▶️')}
                  </motion.button>
                  
                  <motion.button
                    onClick={nextTrack}
                    className="text-white/80 hover:text-white text-2xl"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    ⏭️
                  </motion.button>
                </div>

                {/* Volume Control */}
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-white/70 text-sm">🔊</span>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={volume}
                    onChange={changeVolume}
                    className="flex-1 h-2 bg-white/20 rounded-full appearance-none cursor-pointer music-player-slider"
                  />
                  <span className="text-white/70 text-xs w-8">{Math.round(volume * 100)}</span>
                </div>

                {/* Track List */}
                <div className="max-h-40 overflow-y-auto music-player-scrollbar">
                  <div className="text-white/70 text-sm font-medium mb-2">Playlist ({songs.length} tracks)</div>
                  {songs.map((song, index) => (
                    <motion.div
                      key={song.file}
                      onClick={() => changeTrack(song)}
                      className={`p-2 rounded-lg cursor-pointer transition-all duration-200 ${
                        currentTrack.file === song.file
                          ? 'bg-gradient-to-r from-pink-500/20 to-purple-600/20 text-white'
                          : 'text-white/70 hover:text-white hover:bg-white/5'
                      }`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="text-sm font-medium">{song.title}</div>
                      <div className="text-xs opacity-70">{song.artist}</div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Audio Element */}
      <audio 
        ref={audioRef} 
        src={currentTrack.file} 
        volume={volume}
        preload="metadata"
      />

      {/* Custom styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .music-player-slider::-webkit-slider-thumb {
            appearance: none;
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background: linear-gradient(45deg, #ec4899, #8b5cf6);
            cursor: pointer;
            box-shadow: 0 0 10px rgba(236, 72, 153, 0.5);
          }
          
          .music-player-slider::-moz-range-thumb {
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background: linear-gradient(45deg, #ec4899, #8b5cf6);
            cursor: pointer;
            border: none;
            box-shadow: 0 0 10px rgba(236, 72, 153, 0.5);
          }

          .music-player-scrollbar::-webkit-scrollbar {
            width: 4px;
          }
          
          .music-player-scrollbar::-webkit-scrollbar-track {
            background: rgba(255, 255, 255, 0.1);
            border-radius: 2px;
          }
          
          .music-player-scrollbar::-webkit-scrollbar-thumb {
            background: linear-gradient(45deg, #ec4899, #8b5cf6);
            border-radius: 2px;
          }
          
          .music-player-scrollbar::-webkit-scrollbar-thumb:hover {
            background: linear-gradient(45deg, #be185d, #7c3aed);
          }
        `
      }} />
    </>
  );
}
